import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Skeleton } from "@/components/base/Loading";
import { formatFullDate } from "@/lib/format";
import { downloadCsv, type CsvColumn } from "@/lib/csv";

export interface BatchDevice {
  id: number;
  license_id: number | null;
  device_name: string;
  machine_code: string | null;
  os: string | null;
  status: string;
  released_at: string | null;
}

interface BatchDevicesResponse {
  devices?: BatchDevice[];
  error?: string;
}

interface BatchDeviceListProps {
  batchId: string;
}

const CSV_COLUMNS: CsvColumn<BatchDevice>[] = [
  { key: "device_name", label: "设备名称", value: (row) => row.device_name },
  { key: "os", label: "系统", value: (row) => row.os ?? "" },
  { key: "machine_code", label: "机器码", value: (row) => row.machine_code ?? "" },
  { key: "status", label: "状态", value: (row) => row.status },
  { key: "released_at", label: "解绑时间", value: (row) => (row.released_at ? formatFullDate(row.released_at) : "") },
];

export default function BatchDeviceList({ batchId }: BatchDeviceListProps) {
  const [devices, setDevices] = useState<BatchDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exported, setExported] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke("admin-license", {
          body: { action: "batchDevices", batchId },
        });
        if (invokeError) throw invokeError;
        const result = data as BatchDevicesResponse | null;
        if (result?.error) throw new Error(result.error);
        if (active) setDevices(result?.devices ?? []);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : "批次设备加载失败");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [batchId]);

  const handleExport = () => {
    if (devices.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`批次设备_${batchId.slice(0, 8)}_${stamp}.csv`, CSV_COLUMNS, devices);
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  };

  if (loading) {
    return (
      <div className="mt-2 flex flex-col gap-2 border-l-2 border-accent-200 pl-4">
        {[0, 1].map((item) => (
          <Skeleton key={item} className="h-8 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="mt-2 border-l-2 border-accent-200 pl-4 text-[11px] text-accent-700">{error}</p>;
  }

  if (devices.length === 0) {
    return (
      <p className="mt-2 border-l-2 border-background-200 pl-4 text-[11px] text-foreground-500">
        该批次没有可展示的设备明细（记录可能已过期）。
      </p>
    );
  }

  return (
    <div className="mt-2 border-l-2 border-accent-200 pl-4">
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground-700">
          <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-700">
            <i className="ri-archive-2-line" />
          </span>
          本批次回收设备（{devices.length}）
        </span>
        <button
          type="button"
          onClick={handleExport}
          title="把本次清理回收的设备单独导出存档"
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-800 transition-colors hover:bg-accent-100"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className={exported ? "ri-check-line" : "ri-download-2-line"} />
          </span>
          {exported ? "已导出" : "导出本批次设备"}
        </button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {devices.map((device) => (
          <li
            key={device.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-accent-100 text-accent-700">
                <i className="ri-computer-line text-[11px]" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[11px] font-medium text-foreground-800">{device.device_name}</span>
                {device.os && <span className="block truncate text-[10px] text-foreground-400">{device.os}</span>}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-[10px] text-foreground-500">
              {device.machine_code && <span className="font-mono">{device.machine_code}</span>}
              {device.released_at && <span>{formatFullDate(device.released_at)}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}